import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import type { RootState } from '../index';

// Social Learning Analytics Types
export interface PeerProgressComparison {
  userId: string;
  anonymizedId: string;
  skillLevel: number;
  masteryAreas: string[];
  progressVelocity: number;
  engagementScore: number;
  learningEfficiency: number;
  timeSpent: number;
  achievementsCount: number;
  isCurrentUser: boolean;
  percentileRank: number;
  skillGrowthRate: number;
  collaborationScore: number;
}

export interface AnonymizedBenchmark {
  skillArea: string;
  userPercentile: number;
  averageScore: number;
  topPercentileScore: number;
  userScore: number;
  improvementSuggestions: string[];
  peerComparisons: PeerProgressComparison[];
  confidenceInterval: [number, number];
  sampleSize: number;
  lastUpdated: string;
}

export interface SocialLearningEffectiveness {
  userId: string;
  groupId?: string;
  period: string;
  soloLearningRate: number;
  collaborativeLearningRate: number;
  effectivenessRatio: number; // collaborative / solo
  optimalGroupSize: number;
  bestCollaborationTypes: string[];
  peerInfluenceScore: number;
  knowledgeTransferRate: number;
  socialMotivationBoost: number;
  retentionImprovement: number;
  engagementIncrease: number;
  skillDiversityBenefit: number;
  recommendations: EffectivenessRecommendation[];
}

export interface EffectivenessRecommendation {
  type: 'group_size' | 'activity_type' | 'peer_selection' | 'timing' | 'format';
  title: string;
  description: string;
  expectedImprovement: number;
  confidence: number;
  aiReasoning: string;
  implementationSteps: string[];
}

export interface CollaborativeAchievement {
  id: string;
  title: string;
  description: string;
  type:
    | 'group_milestone'
    | 'peer_support'
    | 'knowledge_sharing'
    | 'collaboration_quality';
  participants: string[];
  groupId?: string;
  earnedAt: string;
  difficulty: 'bronze' | 'silver' | 'gold' | 'platinum';
  points: number;
  requirements: AchievementRequirement[];
  progress: AchievementProgress;
  celebrationMessage: string;
  shareableContent: ShareableContent;
  unlocks: string[];
}

export interface AchievementRequirement {
  type: string;
  description: string;
  target: number;
  current: number;
  completed: boolean;
}

export interface AchievementProgress {
  overallProgress: number;
  individualContributions: Record<string, number>;
  milestones: Milestone[];
  nextMilestone?: Milestone;
  estimatedCompletion: string;
}

export interface Milestone {
  id: string;
  title: string;
  description: string;
  targetProgress: number;
  achieved: boolean;
  achievedAt?: string;
  participants: string[];
  reward?: MilestoneReward;
}

export interface MilestoneReward {
  type: 'points' | 'badge' | 'unlock' | 'feature_access';
  value: number;
  description: string;
  icon: string;
}

export interface ShareableContent {
  title: string;
  description: string;
  imageUrl?: string;
  hashtags: string[];
  template: string;
  customizable: boolean;
}

export interface SocialEngagementOptimization {
  userId: string;
  currentEngagementScore: number;
  potentialEngagementScore: number;
  optimizationOpportunities: EngagementOpportunity[];
  recommendedActions: EngagementAction[];
  peerConnectionSuggestions: PeerSuggestion[];
  groupActivityRecommendations: GroupActivityRecommendation[];
  timingOptimizations: TimingOptimization[];
  contentPersonalization: ContentPersonalization;
  gamificationElements: GamificationElement[];
}

export interface EngagementOpportunity {
  type:
    | 'peer_interaction'
    | 'group_participation'
    | 'content_sharing'
    | 'mentoring';
  title: string;
  description: string;
  potentialImpact: number;
  effort: 'low' | 'medium' | 'high';
  timeframe: string;
  aiReasoning: string;
  successProbability: number;
}

export interface EngagementAction {
  id: string;
  type: string;
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  estimatedImpact: number;
  timeToComplete: number;
  prerequisites: string[];
  trackingMetrics: string[];
}

export interface PeerSuggestion {
  peerId: string;
  peerName: string;
  suggestionType:
    | 'collaboration'
    | 'mentoring'
    | 'study_buddy'
    | 'challenge_partner';
  compatibilityScore: number;
  reasoningFactors: string[];
  suggestedActivities: string[];
  expectedOutcomes: string[];
}

export interface GroupActivityRecommendation {
  activityType: string;
  title: string;
  description: string;
  optimalGroupSize: number;
  estimatedDuration: number;
  skillRequirements: string[];
  engagementPrediction: number;
  learningOutcomes: string[];
}

export interface TimingOptimization {
  activityType: string;
  optimalTimes: string[];
  peakEngagementHours: string[];
  avoidTimes: string[];
  reasoningFactors: string[];
  expectedImprovement: number;
}

export interface ContentPersonalization {
  preferredContentTypes: string[];
  optimalDifficulty: number;
  learningStyleAdaptations: string[];
  interactionPreferences: string[];
  feedbackStyle: string;
}

export interface GamificationElement {
  type: 'points' | 'badges' | 'leaderboards' | 'challenges' | 'streaks';
  title: string;
  description: string;
  motivationFactor: number;
  personalizedFor: string[];
  implementationSuggestion: string;
}

export interface SocialLearningABTest {
  id: string;
  name: string;
  description: string;
  hypothesis: string;
  variants: ABTestVariant[];
  targetMetrics: string[];
  participantCriteria: ParticipantCriteria;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';
  startDate: string;
  endDate?: string;
  results?: ABTestResults;
  confidence: number;
  statisticalSignificance: boolean;
  sampleSize: number;
  currentParticipants: number;
}

export interface ABTestVariant {
  id: string;
  name: string;
  description: string;
  configuration: Record<string, any>;
  trafficAllocation: number;
  participants: number;
  metrics: VariantMetrics;
}

export interface ParticipantCriteria {
  skillLevelRange: [number, number];
  engagementLevelRange: [number, number];
  userTypes: string[];
  excludeGroups: string[];
  minActivityLevel: number;
  geographicRestrictions?: string[];
}

export interface ABTestResults {
  winningVariant?: string;
  confidenceLevel: number;
  effectSize: number;
  pValue: number;
  metricResults: Record<string, MetricResult>;
  recommendations: string[];
  nextSteps: string[];
}

export interface VariantMetrics {
  engagementRate: number;
  completionRate: number;
  retentionRate: number;
  satisfactionScore: number;
  learningEffectiveness: number;
  socialInteractionRate: number;
  timeSpent: number;
  achievementRate: number;
}

export interface MetricResult {
  metric: string;
  controlValue: number;
  variantValue: number;
  improvement: number;
  significanceLevel: number;
  confidenceInterval: [number, number];
}

interface SocialLearningAnalyticsState {
  peerComparisons: AnonymizedBenchmark[];
  learningEffectiveness: SocialLearningEffectiveness | null;
  collaborativeAchievements: CollaborativeAchievement[];
  engagementOptimization: SocialEngagementOptimization | null;
  activeABTests: SocialLearningABTest[];
  completedABTests: SocialLearningABTest[];
  loading: {
    comparisons: boolean;
    effectiveness: boolean;
    achievements: boolean;
    optimization: boolean;
    abTests: boolean;
  };
  error: string | null;
  lastUpdated: string | null;
  analyticsEnabled: boolean;
  privacySettings: AnalyticsPrivacySettings;
}

export interface AnalyticsPrivacySettings {
  allowPeerComparisons: boolean;
  allowAnonymizedBenchmarking: boolean;
  shareProgressData: boolean;
  participateInABTests: boolean;
  dataRetentionPeriod: number; // days
  optOutOptions: string[];
}

const initialState: SocialLearningAnalyticsState = {
  peerComparisons: [],
  learningEffectiveness: null,
  collaborativeAchievements: [],
  engagementOptimization: null,
  activeABTests: [],
  completedABTests: [],
  loading: {
    comparisons: false,
    effectiveness: false,
    achievements: false,
    optimization: false,
    abTests: false,
  },
  error: null,
  lastUpdated: null,
  analyticsEnabled: true,
  privacySettings: {
    allowPeerComparisons: true,
    allowAnonymizedBenchmarking: true,
    shareProgressData: true,
    participateInABTests: true,
    dataRetentionPeriod: 365,
    optOutOptions: [],
  },
};

// Async thunks
export const fetchPeerProgressComparisons = createAsyncThunk(
  'socialLearningAnalytics/fetchPeerComparisons',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `/api/ai/social-analytics/peer-comparisons/${userId}`
      );
      if (!response.ok) {
        throw new Error(
          `Failed to fetch peer comparisons: ${response.statusText}`
        );
      }
      const comparisons: AnonymizedBenchmark[] = await response.json();
      return comparisons;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch peer progress comparisons'
      );
    }
  }
);

export const fetchSocialLearningEffectiveness = createAsyncThunk(
  'socialLearningAnalytics/fetchEffectiveness',
  async (params: { userId: string; period: string }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `/api/ai/social-analytics/effectiveness/${params.userId}?period=${params.period}`
      );
      if (!response.ok) {
        throw new Error(
          `Failed to fetch learning effectiveness: ${response.statusText}`
        );
      }
      const effectiveness: SocialLearningEffectiveness = await response.json();
      return effectiveness;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch social learning effectiveness'
      );
    }
  }
);

export const fetchCollaborativeAchievements = createAsyncThunk(
  'socialLearningAnalytics/fetchAchievements',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `/api/ai/social-analytics/achievements/${userId}`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch achievements: ${response.statusText}`);
      }
      const achievements: CollaborativeAchievement[] = await response.json();
      return achievements;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch collaborative achievements'
      );
    }
  }
);

export const generateEngagementOptimization = createAsyncThunk(
  'socialLearningAnalytics/generateOptimization',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `/api/ai/social-analytics/engagement-optimization`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId }),
        }
      );
      if (!response.ok) {
        throw new Error(
          `Failed to generate optimization: ${response.statusText}`
        );
      }
      const optimization: SocialEngagementOptimization = await response.json();
      return optimization;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to generate engagement optimization'
      );
    }
  }
);

export const fetchActiveSocialABTests = createAsyncThunk(
  'socialLearningAnalytics/fetchActiveABTests',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `/api/ai/social-analytics/ab-tests/active/${userId}`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch AB tests: ${response.statusText}`);
      }
      const abTests: SocialLearningABTest[] = await response.json();
      return abTests;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch active AB tests'
      );
    }
  }
);

export const createSocialLearningABTest = createAsyncThunk(
  'socialLearningAnalytics/createABTest',
  async (
    testConfig: Omit<
      SocialLearningABTest,
      'id' | 'status' | 'results' | 'currentParticipants'
    >,
    { rejectWithValue }
  ) => {
    try {
      const response = await fetch('/api/ai/social-analytics/ab-tests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(testConfig),
      });
      if (!response.ok) {
        throw new Error(`Failed to create AB test: ${response.statusText}`);
      }
      const abTest: SocialLearningABTest = await response.json();
      return abTest;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create AB test');
    }
  }
);

export const updatePrivacySettings = createAsyncThunk(
  'socialLearningAnalytics/updatePrivacySettings',
  async (
    params: { userId: string; settings: Partial<AnalyticsPrivacySettings> },
    { rejectWithValue }
  ) => {
    try {
      const response = await fetch(
        '/api/ai/social-analytics/privacy-settings',
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params),
        }
      );
      if (!response.ok) {
        throw new Error(
          `Failed to update privacy settings: ${response.statusText}`
        );
      }
      const settings: AnalyticsPrivacySettings = await response.json();
      return settings;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to update privacy settings'
      );
    }
  }
);

const socialLearningAnalyticsSlice = createSlice({
  name: 'socialLearningAnalytics',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    toggleAnalytics: (state, action: PayloadAction<boolean>) => {
      state.analyticsEnabled = action.payload;
    },
    updateAchievementProgress: (
      state,
      action: PayloadAction<{
        achievementId: string;
        progress: Partial<AchievementProgress>;
      }>
    ) => {
      const achievement = state.collaborativeAchievements.find(
        a => a.id === action.payload.achievementId
      );
      if (achievement) {
        achievement.progress = {
          ...achievement.progress,
          ...action.payload.progress,
        };
      }
    },
    markMilestoneAchieved: (
      state,
      action: PayloadAction<{ achievementId: string; milestoneId: string }>
    ) => {
      const achievement = state.collaborativeAchievements.find(
        a => a.id === action.payload.achievementId
      );
      if (achievement) {
        const milestone = achievement.progress.milestones.find(
          m => m.id === action.payload.milestoneId
        );
        if (milestone) {
          milestone.achieved = true;
          milestone.achievedAt = new Date().toISOString();
        }
      }
    },
    addCollaborativeAchievement: (
      state,
      action: PayloadAction<CollaborativeAchievement>
    ) => {
      state.collaborativeAchievements.push(action.payload);
    },
    updateABTestMetrics: (
      state,
      action: PayloadAction<{
        testId: string;
        variantId: string;
        metrics: Partial<VariantMetrics>;
      }>
    ) => {
      const test = state.activeABTests.find(
        t => t.id === action.payload.testId
      );
      if (test) {
        const variant = test.variants.find(
          v => v.id === action.payload.variantId
        );
        if (variant) {
          variant.metrics = {
            ...variant.metrics,
            ...action.payload.metrics,
          };
        }
      }
    },
  },
  extraReducers: builder => {
    // Fetch Peer Progress Comparisons
    builder
      .addCase(fetchPeerProgressComparisons.pending, state => {
        state.loading.comparisons = true;
        state.error = null;
      })
      .addCase(fetchPeerProgressComparisons.fulfilled, (state, action) => {
        state.loading.comparisons = false;
        state.peerComparisons = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchPeerProgressComparisons.rejected, (state, action) => {
        state.loading.comparisons = false;
        state.error = action.payload as string;
      });

    // Fetch Social Learning Effectiveness
    builder
      .addCase(fetchSocialLearningEffectiveness.pending, state => {
        state.loading.effectiveness = true;
        state.error = null;
      })
      .addCase(fetchSocialLearningEffectiveness.fulfilled, (state, action) => {
        state.loading.effectiveness = false;
        state.learningEffectiveness = action.payload;
      })
      .addCase(fetchSocialLearningEffectiveness.rejected, (state, action) => {
        state.loading.effectiveness = false;
        state.error = action.payload as string;
      });

    // Fetch Collaborative Achievements
    builder
      .addCase(fetchCollaborativeAchievements.pending, state => {
        state.loading.achievements = true;
        state.error = null;
      })
      .addCase(fetchCollaborativeAchievements.fulfilled, (state, action) => {
        state.loading.achievements = false;
        state.collaborativeAchievements = action.payload;
      })
      .addCase(fetchCollaborativeAchievements.rejected, (state, action) => {
        state.loading.achievements = false;
        state.error = action.payload as string;
      });

    // Generate Engagement Optimization
    builder
      .addCase(generateEngagementOptimization.pending, state => {
        state.loading.optimization = true;
        state.error = null;
      })
      .addCase(generateEngagementOptimization.fulfilled, (state, action) => {
        state.loading.optimization = false;
        state.engagementOptimization = action.payload;
      })
      .addCase(generateEngagementOptimization.rejected, (state, action) => {
        state.loading.optimization = false;
        state.error = action.payload as string;
      });

    // Fetch Active AB Tests
    builder
      .addCase(fetchActiveSocialABTests.pending, state => {
        state.loading.abTests = true;
        state.error = null;
      })
      .addCase(fetchActiveSocialABTests.fulfilled, (state, action) => {
        state.loading.abTests = false;
        state.activeABTests = action.payload;
      })
      .addCase(fetchActiveSocialABTests.rejected, (state, action) => {
        state.loading.abTests = false;
        state.error = action.payload as string;
      });

    // Create AB Test
    builder.addCase(createSocialLearningABTest.fulfilled, (state, action) => {
      state.activeABTests.push(action.payload);
    });

    // Update Privacy Settings
    builder.addCase(updatePrivacySettings.fulfilled, (state, action) => {
      state.privacySettings = action.payload;
    });
  },
});

export const {
  clearError,
  toggleAnalytics,
  updateAchievementProgress,
  markMilestoneAchieved,
  addCollaborativeAchievement,
  updateABTestMetrics,
} = socialLearningAnalyticsSlice.actions;

// Selectors
export const selectPeerComparisons = (state: RootState) =>
  state.socialLearningAnalytics.peerComparisons;
export const selectLearningEffectiveness = (state: RootState) =>
  state.socialLearningAnalytics.learningEffectiveness;
export const selectCollaborativeAchievements = (state: RootState) =>
  state.socialLearningAnalytics.collaborativeAchievements;
export const selectEngagementOptimization = (state: RootState) =>
  state.socialLearningAnalytics.engagementOptimization;
export const selectActiveSocialABTests = (state: RootState) =>
  state.socialLearningAnalytics.activeABTests;
export const selectSocialAnalyticsLoading = (state: RootState) =>
  state.socialLearningAnalytics.loading;
export const selectSocialAnalyticsError = (state: RootState) =>
  state.socialLearningAnalytics.error;
export const selectAnalyticsPrivacySettings = (state: RootState) =>
  state.socialLearningAnalytics.privacySettings;

// Complex selectors
export const selectTopPerformingPeers = createSelector(
  [selectPeerComparisons],
  comparisons => {
    return comparisons
      .flatMap(benchmark => benchmark.peerComparisons)
      .filter(peer => !peer.isCurrentUser)
      .sort((a, b) => b.percentileRank - a.percentileRank)
      .slice(0, 10);
  }
);

export const selectActiveAchievements = createSelector(
  [selectCollaborativeAchievements],
  achievements =>
    achievements.filter(achievement => achievement.progress.overallProgress < 1)
);

export const selectCompletedAchievements = createSelector(
  [selectCollaborativeAchievements],
  achievements =>
    achievements.filter(
      achievement => achievement.progress.overallProgress >= 1
    )
);

export const selectHighImpactOptimizations = createSelector(
  [selectEngagementOptimization],
  optimization => {
    if (!optimization) return [];
    return optimization.optimizationOpportunities
      .filter(opp => opp.potentialImpact >= 0.7)
      .sort((a, b) => b.potentialImpact - a.potentialImpact);
  }
);

export const selectUserPerformanceInsights = createSelector(
  [selectPeerComparisons, selectLearningEffectiveness],
  (comparisons, effectiveness) => {
    const insights = {
      overallRanking: 0,
      strongAreas: [] as string[],
      improvementAreas: [] as string[],
      socialLearningBenefit: 0,
      recommendedActions: [] as string[],
    };

    if (comparisons.length > 0) {
      insights.overallRanking = Math.round(
        comparisons.reduce((sum, comp) => sum + comp.userPercentile, 0) /
          comparisons.length
      );

      insights.strongAreas = comparisons
        .filter(comp => comp.userPercentile >= 75)
        .map(comp => comp.skillArea);

      insights.improvementAreas = comparisons
        .filter(comp => comp.userPercentile < 50)
        .map(comp => comp.skillArea);
    }

    if (effectiveness) {
      insights.socialLearningBenefit = effectiveness.effectivenessRatio;
      insights.recommendedActions = effectiveness.recommendations.map(
        rec => rec.title
      );
    }

    return insights;
  }
);

export default socialLearningAnalyticsSlice.reducer;
